//@flow
import type {WhatsAppWebTypeMessage} from '../../../../entities';

export type MediaType =
  | 'WhatsApp Image Keys'
  | 'WhatsApp Video Keys'
  | 'WhatsApp Audio Keys'
  | 'WhatsApp Document Keys'
  | 'WhatsApp History Keys'
  | 'WhatsApp App State Keys'
  | 'WhatsApp Link Thumbnail Keys';

type MediaKey = {
  iv: Uint8Array,
  cipherKey: Uint8Array,
  macKey: Uint8Array,
  refKey: Uint8Array,
};

const MEDIA_TYPE: {[key: string]: MediaType} = {
  MediaImage: 'WhatsApp Image Keys',
  MediaVideo: 'WhatsApp Video Keys',
  MediaAudio: 'WhatsApp Audio Keys',
  MediaDocument: 'WhatsApp Document Keys',
  MediaHistory: 'WhatsApp History Keys',
  MediaAppState: 'WhatsApp App State Keys',
  MediaLinkThumbnail: 'WhatsApp Link Thumbnail Keys',
};

const getAppInfo = (typeMessage: WhatsAppWebTypeMessage): MediaType => {
  switch (typeMessage) {
    case 'AUDIO':
      return MEDIA_TYPE.MediaAudio;
    case 'VIDEO':
      return MEDIA_TYPE.MediaVideo;
    case 'IMAGE':
    case 'STICKER':
      return MEDIA_TYPE.MediaImage;
    case 'DOCUMENT':
      return MEDIA_TYPE.MediaDocument;

    /* case 'HISTORY':
      return MEDIA_TYPE.MediaHistory;
    case 'APP_STATE':
      return MEDIA_TYPE.MediaAppState;
    case 'LINK_THUMBNAIL':
      return MEDIA_TYPE.MediaLinkThumbnail; */

    default:
      throw new Error('Invalid media type');
  }
};

async function downloadEncryptedMedia(url: string) {
  const response = await fetch(url, {
    headers: {
      Origin: 'https://web.whatsapp.com/',
      Referer: 'https://web.whatsapp.com/',
    },
  });

  const buffer = await response.arrayBuffer();
  const data = new Uint8Array(buffer);

  if (data.length < 10) {
    throw new Error('too short');
  }

  const file = data.slice(0, data.length - 10);
  const mac = data.slice(data.length - 10);

  return {file, mac};
}

async function getMediaKeys(
  mediaKey: Uint8Array,
  appInfo: MediaType
): Promise<MediaKey> {
  const expandedSize = 112 * 8;

  const hkdfKey = await window.crypto.subtle.importKey(
    'raw',
    mediaKey,
    {name: 'HKDF'},
    false,
    ['deriveKey', 'deriveBits']
  );

  const derivedBits = await window.crypto.subtle.deriveBits(
    {
      name: 'HKDF',
      hash: 'SHA-256',
      salt: new Uint8Array(0),
      info: new TextEncoder().encode(appInfo),
    },
    hkdfKey,
    expandedSize
  );

  const derivedBitsArr = new Uint8Array(derivedBits);

  return {
    iv: derivedBitsArr.slice(0, 16),
    cipherKey: derivedBitsArr.slice(16, 48),
    macKey: derivedBitsArr.slice(48, 80),
    refKey: derivedBitsArr.slice(80, 112),
  };
}

async function cbcDecrypt(
  cipherKey: Uint8Array,
  iv: Uint8Array,
  cipherText: Uint8Array
) {
  const cipherKeyImported = await window.crypto.subtle.importKey(
    'raw',
    cipherKey,
    {name: 'AES-CBC'},
    false,
    ['decrypt']
  );

  const decrypted = await window.crypto.subtle.decrypt(
    {
      name: 'AES-CBC',
      iv,
    },
    cipherKeyImported,
    cipherText
  );

  return new Uint8Array(decrypted);
}

function base64ToBytes(base64: string): Uint8Array {
  const binaryString = atob(base64);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
}

export async function decryptMedia(
  url: string,
  mediaKeyStr: string,
  typeMessage: WhatsAppWebTypeMessage,
  mimetype: string
): Promise<Blob> {
  const mediaKeyBytes = base64ToBytes(mediaKeyStr);
  const appInfo = getAppInfo(typeMessage);

  const mediaKey = await getMediaKeys(mediaKeyBytes, appInfo);
  const {file} = await downloadEncryptedMedia(url);

  const plainText = await cbcDecrypt(mediaKey.cipherKey, mediaKey.iv, file);

  return new Blob([plainText], {type: mimetype});
}
